import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { yourEvaluationTab, overallScoreInputType, allEvaluationsTab, DEFAULT_SECTION_ID, evaluationsSummaryTab } from './config';
import { createConfig, getFilteredSteps, mapAttributesList } from './utils';
import fetchCustom from '../../wp_helpers/fetchCustom';
import { useTranslations } from '../context/TranslationContext';
import { getApplicationEvaluationConfig } from './ApplicantEvaluationWrapper/service';
import getResponseJson from '../../wp_helpers/getResponseJson';
import storageStore from '../../wp_helpers/storage/store';

const ApplicantEvaluation = React.createContext(null);

const ApplicantEvaluationProvider = ({ children, applicantId, onEvaluationUpdated = () => { } }) => {
    const { t } = useTranslations();
    const config = useRef({
        ratingScales: {},
        getScoreOption: () => { },
        sections: [], // {'id': 'my_evaluations', 'text': 'Your evaluations', 'tabs': [{'id': 'your_evaluation', 'text': 'Your evaluation'}]}
        overallScoreAttribute: null,
    });
    const [applicantInfo, setApplicantInfo] = useState({});
    const [evaluationConfig, setEvaluationConfig] = useState({ job_id: null, currentStep: null });
    const { applicant_current_step: currentStep, job_id, max_evaluation_note_length } = evaluationConfig;
    const [activeTab, setActiveTab] = useState(null);
    const [activeSection, setActiveSection] = useState(null);
    const [evaluationCriteria, setEvaluationCriteria] = useState([]);
    const [loading, setLoading] = useState(true);
    const [readOnly, setReadOnly] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selectedCriteria, setSelectedCriteria] = useState(null);
    const [recruitmentFlow, setRecruitmentFlow] = useState({});
    const [step, setStep] = useState({});
    const [stepsEvaluations, setStepsEvaluations] = useState({});
    const [summary, setSummary] = useState({});
    const [stepsCollapsed, setStepsCollapsed] = useState({});
    const [showOtherStepOverlay, setShowOtherStepOverlay] = useState(true);
    const [permissions, setPermissions] = useState({});

    const canEvaluate = evaluationConfig?.put_applicant_evaluation_url;

    const attributesList = useRef({});
    const { overallScoreAttribute, sections = [] } = config.current || {};

    const getAttributeTitle = useCallback((attribute) => {
        if (!attribute) return '';
        if (attribute in attributesList.current) return attributesList.current[attribute];
        const [_, ...values] = attribute.split(':');
        if (attribute == overallScoreAttribute) {
            return t('Overall step impression');
        }
        return values.join(':');
    }, [t, overallScoreAttribute]);

    const getEvaluationResult = async (step_id) => {
        try {
            const response = await fetchCustom(
                evaluationConfig.get_applicant_evaluation_url.replace(':step', step_id)
            );
            const { evaluation_result, is_evaluated, can = {} } = await response.json();
            setStepsEvaluations({ ...stepsEvaluations, [step_id]: evaluation_result });
            setPermissions(can);
            setReadOnly(is_evaluated);
        } catch (e) {
            console.error('Error while fetching the evaluation', e);
            notification(global_i18n('ajax_fail_notification'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            const data = await getApplicationEvaluationConfig(applicantId);
            setEvaluationConfig(data);
        };
        fetchData();
    }, [applicantId]);

    useEffect(() => {
        const tab = activeTab || sections?.[0]?.['tabs']?.[0]?.['id'];
        setActiveSection(sections?.find(({ tabs = [] }) => tabs?.find(({ id }) => id == tab)) || sections?.[0] || {});
        if (!activeTab) {
            setActiveTab(tab);
        }
    }, [activeTab, sections]);

    const getLatestCurrentStep = useCallback(async () => {
        const data = await getApplicationEvaluationConfig(applicantId);
        return data.applicant_current_step;
    }, [applicantId]);

    const steps = useMemo(() => {
        if (!recruitmentFlow?.steps) return [];
        const filteredSteps = getFilteredSteps(recruitmentFlow.steps, evaluationCriteria);
        return filteredSteps;
    }, [recruitmentFlow, evaluationCriteria]);

    useEffect(() => {
        if (steps) {
            const defaultStep = steps.find(({ id }) => id === currentStep);
            setStep(defaultStep || steps[0] || {});
        }
    }, [steps]);

    useEffect(() => {
        const init = async () => {
            const { api_get_applicant_evaluation_criteria } = evaluationConfig || {};
            if (!api_get_applicant_evaluation_criteria) {
                return;
            }
            try {
                // TODO: this should be handled via store (after merging the PR into applicant_view-integration)
                const evaluationsRes = await fetchCustom(api_get_applicant_evaluation_criteria);
                const queryParams = [
                    'expand=ratings',
                    'expand=application_feedback',
                    'expand=recruitment_flow',
                ].join('&');
                const applicantRes = await fetchCustom(`/api/applications/${applicantId}?${queryParams}`);
                const _evaluationCriteria = await getResponseJson(evaluationsRes);
                const _applicantInfo = await getResponseJson(applicantRes);
                const recruitmentFlowUrl = _applicantInfo?.recruitment_flow?._links?.get?.url;
                if (recruitmentFlowUrl) {
                    const recruitmentFlowRes = await fetchCustom(recruitmentFlowUrl);
                    const recruitmentFlow = await getResponseJson(recruitmentFlowRes);
                    if (!recruitmentFlowRes.ok) {
                        setLoading(false);
                        return false;
                    }
                    setRecruitmentFlow(recruitmentFlow);
                }
                if (!evaluationsRes.ok || !applicantRes.ok) {
                    setLoading(false);
                    return false
                }
                const { evaluation_criteria, attributes, ...rest } = _evaluationCriteria;
                config.current = createConfig(rest);
                attributesList.current = mapAttributesList(attributes);
                setApplicantInfo(_applicantInfo);
                // Get the current selected step of job and set it to default
                setEvaluationCriteria(evaluation_criteria);
                setLoading(!!steps.length);
            } catch (e) {
                console.error('Error while fetching data', e);
                notification(global_i18n('ajax_fail_notification'), { type: 'error' });
            }
        };

        init();
    }, [evaluationConfig]);

    const onChangeStepHandler = useCallback(
        (stepId) => {
            setStep(steps.find(({ id }) => id === stepId));
            if (!activeTab) {
                setActiveTab(sections?.[0]?.['tabs']?.[0]?.['id']);
            }
        },
        [steps, activeTab, sections]
    );

    const canShowAnotherStepOverlay = useCallback((jobId) => {
        const prevData = new Set(storageStore.evaluation.getAnotherStepOverlay());
        return !prevData.has(jobId);
    }, []);

    const setShowAnotherStepOverlay = (jobId) => {
        const prevData = new Set(storageStore.evaluation.getAnotherStepOverlay());
        prevData.add(jobId);
        storageStore.evaluation.setAnotherStepOverlay(Array.from(prevData));
    };

    const showAnotherStepConfirmationOverlay = (callback) => {
        const checkboxId = 'do-not-show-again-checkbox';
        show_confirmation_overlay(
            'confirm-another-step-review',
            t('Applicant is in a different step'),
            t(
                'This applicant is currently in a different step of the process. Are you sure you want to add an evaluation in this step?'
            ),
            {
                label: t('Cancel'),
                classes: 'overlay-cancel-link-show',
            },
            {
                label: t('Confirm'),
                action: function () {
                    const doNotShowAgain = document.getElementById(checkboxId)?.checked;
                    if (doNotShowAgain) {
                        setShowAnotherStepOverlay(job_id);
                    }
                    if (callback) {
                        callback();
                    }
                },
            },
            null,
            `<div class="form-group">
                <label for="${checkboxId}"><input type="checkbox" id="${checkboxId}">
                    ${t("Don't show again")}
                </label>
            </div>`
        );
    };

    const onSaveHandler = async (saveAsDraft) => {
        const evaluation = stepsEvaluations[step.id] || {};
        if (saveAsDraft) {
            return saveEvaluation(saveAsDraft);
        }
        if (!hasOverallImpressionRating(evaluation) && !saveAsDraft) {
            notification(t('Please rate the overall impression'), { type: 'warning' });
            return;
        }
        showConfirmSubmitEvaluationOverlay();
    };

    const showConfirmSubmitEvaluationOverlay = () => {
        show_confirmation_overlay(
            'confirm-evaluation-submission',
            t('Submit Evaluation'),
            t(
                'Are you sure you want to submit the evaluation? Once submitted, it cannot be edited.'
            ),
            { label: t('Cancel') },
            {
                label: t('Submit'),
                action: function () {
                    saveEvaluation();
                },
            }
        );
    };

    const hasOverallImpressionRating = (evaluation) => {
        const overallImpressionAttribute = evaluation[overallScoreAttribute];
        return Number.isFinite(overallImpressionAttribute?.score);
    };

    const saveEvaluation = async (saveAsDraft) => {
        if (saving) return;
        setSaving(true);
        const evaluation = stepsEvaluations[step.id] || {};
        const evaluationData = Object.entries(evaluation).reduce(
            (acc, [attribute, { score, answer, score_type }]) => {
                acc[attribute] = {
                    score_type,
                    score,
                    answer,
                };
                return acc;
            },
            {}
        );
        try {
            const response = await fetchCustom(
                evaluationConfig?.put_applicant_evaluation_url?.replace(':step', step.id),
                {
                    method: 'PUT',
                    body: JSON.stringify({
                        save_as_draft: !!saveAsDraft,
                        evaluation: evaluationData,
                    }),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!response.ok) {
                notification(global_i18n('ajax_fail_notification'), { type: 'error' });
                throw new Error(`Network response was not ok: ${response.statusText}`);
            }
            const res = await response.json();
            notification(res.notification, { type: 'success' });
            getEvaluationResult(step.id);
            if (!saveAsDraft) {
                setActiveTab(allEvaluationsTab);
            }
            onEvaluationUpdated();
        } catch (e) {
            console.error('Error saving eval', e);
            notification(global_i18n('ajax_fail_notification'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const evaluationStarted = useCallback(() => {
        if (!permissions?.change_application_steps
            || recruitmentFlow.current.type !== 'NewApplication'
            || applicantInfo.outcome
        ) {
            return
        }
        const feedbackSend = applicantInfo?.application_feedback?.sent_feedback;
        if (typeof window.recruitment_steps?.evaluationStarted === 'function' && !feedbackSend) {
            window.recruitment_steps.evaluationStarted({ preventReload: true });
        } else {
            console.error('evaluationStarted is not a function');
        }
    }, [permissions, recruitmentFlow, applicantInfo]);

    const updateEvaluationState = useCallback(
        (attribute, key, value) => {
            if (readOnly) return;
            const { id } = step;
            const items = selectedCriteria?.items ?? [];
            const scoreTypes = items.reduce((acc, { attribute, score_type }) => {
                acc[attribute] = score_type;
                return acc;
            }, {});

            scoreTypes[overallScoreAttribute] = overallScoreInputType;

            setStepsEvaluations((prevEvaluations) => {
                const stepEvaluation = prevEvaluations[id];
                return {
                    ...prevEvaluations,
                    [id]: {
                        ...stepEvaluation,
                        [attribute]: {
                            ...stepEvaluation?.[attribute],
                            [key]: value,
                            score_type: scoreTypes[attribute],
                        },
                    },
                };
            });
        },
        [readOnly, step, selectedCriteria, overallScoreAttribute, overallScoreInputType]
    );

    const onStepEvaluationChange = useCallback(
        async (attribute, key, value) => {
            if (readOnly) return;
            //TODO: fix this, as we need to only send the email to user, no need to move to other step.
            // evaluationStarted();
            const performAction = () => {
                updateEvaluationState(attribute, key, value);
            }
            const currentStep = await getLatestCurrentStep();
            if (currentStep !== step.id && canShowAnotherStepOverlay(job_id) && showOtherStepOverlay) {
                setShowOtherStepOverlay(false);
                showAnotherStepConfirmationOverlay(performAction);
                return;
            }
            performAction();
        },
        [readOnly, step.id, showOtherStepOverlay, updateEvaluationState, getLatestCurrentStep, canShowAnotherStepOverlay, job_id]
    );

    const fetchSummary = async () => {
        const response = await fetchCustom(evaluationConfig.get_applicant_evaluation_summary_url.replace(':step', step.id));
        if (!response.ok) {
            notification(global_i18n('ajax_fail_notification'), { type: 'error' });
            return;
        }
        setSummary(await response.json());
    };

    useEffect(() => {
        if (step.id) {
            setLoading(true);
            setSelectedCriteria(evaluationCriteria.find(({ step_id }) => step_id === step.id));
            getEvaluationResult(step.id);
        }
    }, [step, evaluationCriteria]);

    useEffect(() => {
        const isSummaryTab =
            activeTab === evaluationsSummaryTab ||
            activeTab === allEvaluationsTab;

        if (!isSummaryTab) return;

        fetchSummary();

    }, [activeTab, step]);

    const selectedStepEvaluation = useMemo(
        () => stepsEvaluations[step.id] || {},
        [stepsEvaluations, step]
    );

    const isCollapsed = stepsCollapsed[step?.id];
    const toggleCollapsed = useCallback(() => {
        setStepsCollapsed((prevData) => ({ ...prevData, [step?.id]: !prevData[step?.id] }));
    }, [step, stepsCollapsed]);

    const hasAnyQuestions = useMemo(() => {
        return selectedCriteria?.items?.some(({ question }) => !!question);
    }, [selectedCriteria]);

    if (!applicantId) {
        console.error('ApplicantEvaluationProvider: applicantId is required');
        return null;
    }

    return (
        <ApplicantEvaluation.Provider
            value={{
                activeTab,
                getAttributeTitle,
                isCollapsed,
                loading,
                onChangeStepHandler,
                onSaveHandler,
                onStepEvaluationChange,
                updateEvaluationState,
                readOnly,
                selectedCriteria,
                selectedStepEvaluation,
                setActiveTab,
                step,
                steps,
                stepsEvaluations,
                summary,
                toggleCollapsed,
                saving,
                applicantInfo,
                max_evaluation_note_length,
                permissions,
                recruitmentFlow,
                onEvaluationUpdated,
                activeSection,
                setActiveSection,
                hasAnyQuestions,
                ...config.current,
            }}
        >
            {children}
        </ApplicantEvaluation.Provider>
    );
};

export const useApplicantEvaluation = () => {
    const context = useContext(ApplicantEvaluation);
    if (!context) {
        throw new Error('useApplicantEvaluation must be used within a ApplicantEvaluationProvider');
    }
    return context;
};

export default ApplicantEvaluationProvider;
